import { endOfDay, startOfDay } from 'date-fns';
import { useUpdateTask } from 'features/tasks';
import { useCallback, useMemo } from 'react';
import {
  DayOrder,
  useUpdateTodoDaysOrderMutation,
} from 'shared/hooks/queries/use-update-todo-days-order-mutation';
import { useUser } from 'shared/hooks/use-user';
import {
  DateSectionOptions,
  TaskCardDateSection,
} from 'shared/types/orderable-section';
import { TaskCardTask } from 'shared/types/task-card-task';
import { getTaskPlanning } from 'shared/utils/get-task-planning';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { createPlannedOccurrenceForDate } from 'shared/utils/schedules';

export const useHandleDateSectionDrop = (sections: TaskCardDateSection[]) => {
  const { submit: updateTask } = useUpdateTask();
  const { mutate: updateOrder } = useUpdateTodoDaysOrderMutation();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const oldSections = useMemo(() => structuredClone(sections), [sections]);

  return useCallback(
    (newSections: TaskCardDateSection[], movedTask: TaskCardTask) => {
      const newSection = newSections.find(({ items }) =>
        items.find(({ id }) => id === movedTask.id),
      );

      const oldSection = oldSections.find(({ items }) =>
        items.find(({ id }) => id === movedTask.id),
      );

      if (!newSection || !oldSection) {
        // data error
        return;
      }

      if (
        movedTask.raw.task.schedules?.length &&
        newSection.id !== oldSection.id
      ) {
        // moving a habit into another date means adding a planned occurrence to the task, with the original date as the one to move it from
        const [plannedOccurrence] = getTaskPlanning(movedTask.raw.task, {
          startDate: startOfDay(oldSection.deadline),
          endDate: endOfDay(oldSection.deadline),
          weekStartsOn,
        });

        const schedules = movedTask.raw.task.schedules
          ? createPlannedOccurrenceForDate(
              movedTask.raw.task.schedules,
              newSection.deadline,
              {
                timeslot: plannedOccurrence?.timeslot ?? null,
                originalDate: oldSection.deadline,
                weekStartsOn,
              },
            )
          : undefined;

        updateTask({
          id: movedTask.raw.task.id,
          schedules,
        });
      }

      // check whether the task has moved between sections to update the deadline
      if (
        !movedTask.raw.task.schedules?.length &&
        newSection.deadline.getTime() !== oldSection.deadline.getTime()
      ) {
        // update the task with the new deadline
        updateTask({
          id: movedTask.raw.task.id,
          endStrategy: {
            deadline: newSection.deadline,
            completionCount:
              movedTask.raw.task.endStrategy?.completionCount ?? null,
          },
          schedules: null,
        });
      }

      // update order if the updated or old section is one of the first 7 days
      if (
        newSection.type === DateSectionOptions.Day ||
        oldSection.type === DateSectionOptions.Day
      ) {
        const oldSectionOrderKey =
          newSection.id !== oldSection.id ? oldSection.id : undefined;
        const oldSectionOrder = user?.settings?.todoSorting?.[
          oldSectionOrderKey as string
        ]?.filter((id) => id !== movedTask.raw.task.id);

        const ordersToUpdate: DayOrder[] = [
          newSection.type === DateSectionOptions.Day
            ? {
                date: newSection.deadline,
                ids: newSection.items.map(({ raw }) => raw.task.id),
              }
            : undefined,
          oldSectionOrder
            ? {
                date: oldSection.deadline,
                ids: oldSectionOrder,
              }
            : undefined,
        ].filter(Boolean) as DayOrder[];

        updateOrder(ordersToUpdate);
      }
    },
    [
      oldSections,
      weekStartsOn,
      updateTask,
      user?.settings?.todoSorting,
      updateOrder,
    ],
  );
};
